import "./style.scss";
import PopupHeader from "../../PopupHeader";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
// import { getUIComponent } from "@builder/store/slices/BuilderSlice/builderMap";
import Button from "@builder/components/general/Button";
import OliverText from "@builder/components/general/OliverText";
import { Fragment, memo, useCallback, useEffect, useState } from "react";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import TextInput from "@builder/components/general/TextInput";
import { Divider } from "@mui/material";
import ConditionSelectors from "@builder/components/general/ConditionSelectors";
import useConditions from "@builder/hooks/useConditions";
import { actionSelectOptions } from "./utils";
import { useForm } from "react-hook-form";
import { updateCondition, updateConditionalActions } from "@builder/store/slices/BuilderSlice/builderSlice";
import {
    convertConditionsToString,
    getActionType,
    getConditionActionValue,
    isEmpty,
    validateConditions
} from "@builder/utils/conditionsHelpers";
import { v4 } from "uuid";
import { DefaultValuesCondition, closePopup } from "@builder/store/slices/popupSlice";
import { ConditionsDataProvider } from "@builder/context/ConditionsData";
import { Action, ActionName } from "@common/build/types";
import SelectInput from "@builder/components/general/ConditionSelectInput";
import { CONDITION_ACTION } from "@builder/utils/constants";

const Conditions: React.FC = memo(() => {
    const selectedNode = useAppSelector(getSelectedNode);
    const defaultValues: DefaultValuesCondition | null = useAppSelector(state => state.popup.defaultValues);
    const a = selectedNode?.actions?.find(action => action.id === defaultValues?.actionId);

    const dispatch = useAppDispatch();
    const [conditionName, setConditionName] = useState<string>(defaultValues?.name || "");
    const [conditionActionValue, setConditionActionValue] = useState<string>(getConditionActionValue(a?.action.type || null));
    const { state, handleSelect, addCondition, addGroup, removeCondition } = useConditions(defaultValues?.conditionState);
    const [error, setError] = useState<Record<number, Record<string, boolean>> | null>(null);
    const [submitted, setSubmitted] = useState<boolean>(false);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm();

    const handleInput = useCallback((value: string): void => {
        setConditionName(value);
    }, []);

    useEffect(() => {
        if (submitted) {
            const errorStates = validateConditions(state);
            setError(errorStates);
        }
    }, [state, submitted]);

    const onSubmit = () => {
        setSubmitted(true);
        const errorStates = validateConditions(state);

        setError(errorStates);

        if (!isEmpty(errorStates)) return;

        const action = getActionType(conditionActionValue as CONDITION_ACTION);

        const conditionAction: Action = {
            id: defaultValues?.actionId || v4(),
            name: ActionName.ON_CONDITION,
            action: action
        };

        const cc: { id: string; name: string; conditions: string; actionId: string } = {
            id: defaultValues?.id || v4(),
            name: conditionName,
            conditions: convertConditionsToString(state),
            actionId: conditionAction.id
        };

        dispatch(updateCondition(cc));
        dispatch(updateConditionalActions(conditionAction));

        dispatch(closePopup());
    };

    return (
        <div className="conditions-c">
            <PopupHeader
                src="/fork.svg"
                title={`${defaultValues ? "Edit Conditions" : "Conditions"}`}
                subtext={selectedNode ? selectedNode.name : "baseLayerPage"}
            >
                <Button
                    buttonStyle="primary"
                    text="Apply Conditions"
                    size="small"
                    className="conditions-header-button"
                    onClick={handleSubmit(onSubmit)}
                />
            </PopupHeader>
            <div className="conditions-body">
                <div className="conditions-tooltip">
                    <OliverText text="Conditions" size="s" weight="semi-bold" />
                    <OliverText
                        size="xs"
                        weight="medium"
                        text="Lorem ipsum dolor sit amet, consectetur adipiscing elit.Lorem ipsum dolor sit amet, consectetur adipiscing elit.Lorem ipsum dolor sit amet, consectetur adipiscing elit.Lorem ipsum dolor sit amet, consectetur adipiscing elit."
                    />
                    <span>
                        <OliverText size="xs" weight="medium" text="You can learn more about product data" />
                        <a href="#">here</a>
                    </span>
                </div>
                <div className="conditions-apply">
                    <div className="col">
                        <OliverText size="s" weight="semi-bold" text="Condition name" />
                        <OliverText
                            size="xs"
                            weight="medium"
                            text="Select the location your register is tied to and, by extension, the available inventory."
                        />
                    </div>
                    <div className="col">
                        <TextInput
                            name="conditionName"
                            register={register}
                            type="change"
                            value={conditionName}
                            callback={handleInput}
                            placeholder="Enter condition name"
                            rules={{ required: true }}
                            error={"conditionName" in errors}
                        />
                    </div>
                </div>
                <div className="conditions-apply">
                    <div className="col">
                        <OliverText size="s" weight="semi-bold" text="If conditions apply" />
                        <OliverText
                            size="xs"
                            weight="medium"
                            text="Select the location your register is tied to and, by extension, the available inventory."
                        />
                    </div>
                    <div className="col">
                        <SelectInput
                            className="conditions-action"
                            value={conditionActionValue}
                            callback={value => setConditionActionValue(value)}
                            options={actionSelectOptions}
                            action
                        />
                    </div>
                </div>
                <div className="conditions-body scroll">
                    {state.conditions.map((condition, groupIndex) => (
                        <Fragment key={groupIndex}>
                            <div className="conditions-selectors-wrapper">
                                <ConditionsDataProvider>
                                    <ConditionSelectors
                                        error={error}
                                        isEdit={defaultValues != null}
                                        conditions={condition}
                                        handleSelect={(index, key, value) => handleSelect(groupIndex, index, key, value)}
                                        removeCondition={index => removeCondition(groupIndex, index)}
                                        addCondition={() => addCondition(groupIndex)}
                                        groupIndex={groupIndex}
                                    />
                                </ConditionsDataProvider>
                            </div>
                            {condition && condition.length > 0 && (
                                <div className="decision-selectors">
                                    <button className="decision-button" onClick={() => addCondition(groupIndex)}>
                                        <img src="/plus-conditions.svg" />
                                        AND
                                    </button>
                                    {groupIndex < state.conditions.length - 1 ? (
                                        <Divider
                                            style={{ width: "532px", fontFamily: "Poppins", color: "var(--theme-text-color)" }}
                                        >
                                            OR
                                        </Divider>
                                    ) : (
                                        <Divider style={{ width: "532px" }} />
                                    )}
                                </div>
                            )}
                        </Fragment>
                    ))}
                    <div className="decision-selectors">
                        <button className="decision-button" onClick={addGroup}>
                            <img src="/plus-conditions.svg" />
                            Add condition (OR)
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Conditions;
